import type {
  CardSize,
  CardSizeId,
  DuplexPresetId,
  MarkSettings,
  PaperSize,
  PaperSizeId,
  PrinterSettings,
} from '../types';

export const DPI = 300;

export const MM_PER_IN = 25.4;

export const inToPx = (inches: number) => Math.round(inches * DPI);
export const mmToPx = (mm: number) => Math.round((mm / MM_PER_IN) * DPI);
export const mmToIn = (mm: number) => mm / MM_PER_IN;

// Paper sizes always rendered landscape — width is the long edge.
export const PAPER_SIZES: Record<Exclude<PaperSizeId, 'custom'>, PaperSize> = {
  letter: {
    id: 'letter',
    label: 'Letter',
    widthIn: 11.0,
    heightIn: 8.5,
    widthPx: inToPx(11.0),
    heightPx: inToPx(8.5),
  },
  a4: {
    id: 'a4',
    label: 'A4',
    widthIn: 11.69,
    heightIn: 8.27,
    widthPx: inToPx(11.69),
    heightPx: inToPx(8.27),
  },
};

export const PAPER_SIZE_LIST = [PAPER_SIZES.letter, PAPER_SIZES.a4];

// Custom paper: store landscape-normalized (long edge as width) so spread
// math is consistent with Letter/A4. Caller may enter portrait dims.
export const buildCustomPaperSize = (dim1In: number, dim2In: number): PaperSize => {
  const widthIn = Math.max(dim1In, dim2In);
  const heightIn = Math.min(dim1In, dim2In);
  return {
    id: 'custom',
    label: `Custom ${widthIn}×${heightIn} in`,
    widthIn,
    heightIn,
    widthPx: inToPx(widthIn),
    heightPx: inToPx(heightIn),
  };
};

// Card sizes describe ONE panel — width is the folded-card width.
// Spread width on the page = 2 × widthIn.
export const CARD_SIZES: Record<Exclude<CardSizeId, 'custom'>, CardSize> = {
  '5x7': { id: '5x7', label: '5×7', widthIn: 5.0, heightIn: 7.0, widthPx: inToPx(5.0), heightPx: inToPx(7.0) },
  a2: { id: 'a2', label: 'A2', widthIn: 4.25, heightIn: 5.5, widthPx: inToPx(4.25), heightPx: inToPx(5.5) },
  poker: { id: 'poker', label: 'Poker', widthIn: 2.5, heightIn: 3.5, widthPx: inToPx(2.5), heightPx: inToPx(3.5) },
  square5: { id: 'square5', label: 'Square 5×5', widthIn: 5.0, heightIn: 5.0, widthPx: inToPx(5.0), heightPx: inToPx(5.0) },
};

export const buildCustomCardSize = (widthIn: number, heightIn: number): CardSize => ({
  id: 'custom',
  label: `Custom ${widthIn}×${heightIn}`,
  widthIn,
  heightIn,
  widthPx: inToPx(widthIn),
  heightPx: inToPx(heightIn),
});

export const CARD_SIZE_LIST = [
  CARD_SIZES['5x7'],
  CARD_SIZES.a2,
  CARD_SIZES.poker,
  CARD_SIZES.square5,
];

export const CROP_MARK_LEN_PX = mmToPx(5);
export const FOLD_LINE_EXTEND_PX = mmToPx(5);

export const MIN_DPI_WARN = 150;

// ===== Mark + printer setting defaults / presets =====

export const DEFAULT_MARKS: MarkSettings = {
  cornerMarks: true,
  foldLine: true,
  cardBorder: true,
};

// Short-edge flip (default for greeting cards): inside images get swapped
// left↔right because the printer's mechanical flip mirrors them horizontally.
export const PRESET_SHORT_EDGE: PrinterSettings = {
  outside: { swapLR: false, rotate180: false },
  inside: { swapLR: true, rotate180: false },
};

// Long-edge flip: the printer's flip mirrors top↔bottom instead of left↔right.
// To compensate, each inside panel is rotated 180° (no left/right swap).
export const PRESET_LONG_EDGE: PrinterSettings = {
  outside: { swapLR: false, rotate180: false },
  inside: { swapLR: false, rotate180: true },
};

// Two-sheet glue: outside on sheet 1, inside on sheet 2 in natural orientation
// — no flip mechanic to compensate for. Glue the sheets back-to-back.
export const PRESET_TWO_SHEET: PrinterSettings = {
  outside: { swapLR: false, rotate180: false },
  inside: { swapLR: false, rotate180: false },
};

export const DUPLEX_PRESETS: Array<{ id: DuplexPresetId; label: string; settings: PrinterSettings }> = [
  { id: 'short-edge', label: 'Short-edge flip', settings: PRESET_SHORT_EDGE },
  { id: 'long-edge', label: 'Long-edge flip', settings: PRESET_LONG_EDGE },
  { id: 'two-sheet', label: 'Two-sheet (no flip)', settings: PRESET_TWO_SHEET },
];

export const matchPreset = (s: PrinterSettings): DuplexPresetId | null => {
  for (const preset of DUPLEX_PRESETS) {
    const p = preset.settings;
    if (
      p.outside.swapLR === s.outside.swapLR &&
      p.outside.rotate180 === s.outside.rotate180 &&
      p.inside.swapLR === s.inside.swapLR &&
      p.inside.rotate180 === s.inside.rotate180
    ) {
      return preset.id;
    }
  }
  return null;
};
